import LeBell from "@dsk/icons/LeBell.vue";
import { toRaw } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import LeSearchChangedLanguageSelectorToast from "@/components/toasts/LeSearchChangedLanguageSelectorToast.vue";
import { MatomoHelper, SearchFilterHelper, SearchHelper, SearchRouteHelper } from "@/helpers";
import { i18n } from "@/i18n";
import { useDictionariesStore, useSearchFilterStore, useSearchStore, useUserStore } from "@/stores";
import { type Dictionary, MatomoEventAction, MatomoEventCategory } from "@/types";

export function useSearchFromResult() {
  const searchFilterStore = useSearchFilterStore();
  const dictionariesStore = useDictionariesStore();
  const searchStore = useSearchStore();

  const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);

  const searchFromFuzzy = (query: string): void => {
    searchRouteHelper.search(query, searchStore.filter, searchStore.selectedDictionaryId);
  };

  const searchOppositeDirection = (query: string, dictionary?: Dictionary): void => {
    const searchFilter = searchStore.filter;

    if (!searchFilter) {
      searchRouteHelper.search(query, searchFilter, dictionary?.id);
      return;
    }

    const swappedFilter = SearchFilterHelper.swap(searchFilter, dictionary);

    // Show toast if filter was swapped
    if (swappedFilter) {
      showSearchFilterChangedToast();
    }

    const filter = swappedFilter ?? searchFilter;
    if (dictionary) {
      searchRouteHelper.searchWithChangedSearchFilter(query, filter, dictionary.id);
      return;
    }

    searchRouteHelper.search(query, filter);
  };

  const classicSearchFromResult = (
    query: string,
    methodId?: string,
    auto = true,
    staticMetadata?: string,
    temporaryMetadata?: string[],
    section?: string
  ) => {
    let dictionary: Dictionary | undefined;

    if (methodId) {
      dictionary = dictionariesStore.getDictionaryByMethodId(methodId ?? "");
    } else {
      dictionary = searchStore.selectedDictionary;
    }

    const direction = auto ? searchStore.direction : methodId;

    let filter = searchStore.filter;

    // Search filter needs to be swapped for bilingual dictionaries with links to the other direction
    if (filter && direction !== searchStore.direction) {
      const swappedFilter = SearchFilterHelper.swap(filter, dictionary);

      if (swappedFilter) {
        filter = swappedFilter;
        showSearchFilterChangedToast();
      }
    }

    const currentSearch = {
      query: searchStore.query,
      filter: searchStore.filter,
      staticMetadata: toRaw(searchStore.staticMetadata),
      temporaryMetadata: toRaw(searchStore.temporaryMetadata),
      section: searchStore.section
    };

    const nextSearch = {
      query: query,
      filter: searchFilterStore.filter,
      staticMetadata: searchRouteHelper.buildNextStaticMetadata(direction, staticMetadata),
      temporaryMetadata: searchRouteHelper.buildNextTemporaryMetadata(direction, temporaryMetadata),
      section: section
    };

    searchRouteHelper.search(
      query,
      filter,
      dictionary?.id,
      staticMetadata,
      temporaryMetadata,
      section,
      SearchHelper.isNewSearch(currentSearch, nextSearch),
      false
    );
  };

  const showSearchFilterChangedToast = (): void => {
    const toast = useToast();

    toast.clear();
    toast(
      {
        component: LeSearchChangedLanguageSelectorToast,
        props: { i18n: i18n.global, type: "changed-direction" }
      },
      { timeout: 20000, icon: LeBell }
    );

    MatomoHelper.sendEvent(
      MatomoEventCategory.Search,
      MatomoEventAction.ToastDisplayed,
      "Change of language selector direction toast displayed"
    );
  };

  return {
    searchFromFuzzy,
    searchOppositeDirection,
    classicSearchFromResult
  };
}
